import { FC } from 'react';
import { useFragment } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Form from 'components/Form';
import AccordionController from 'components/AccordionController';
import General from 'components/GeneralSettingsEditFormGeneral';
import Seo from 'components/GeneralSettingsEditFormSeo';
import Smtp from 'components/GeneralSettingsEditFormSmtp';
import OpenGraph from 'components/GeneralSettingsEditFormOpenGraph';
import Flights from 'components/GeneralSettingsEditFormFlights';
import System from 'components/GeneralSettingsEditFormSystem';

import { textFieldValue } from 'lib';

import {
  SmtpType,
  FlightsProvider,
  GeneralSettingsEditForm_settings$key,
} from '__generated__/GeneralSettingsEditForm_settings.graphql';
import { Range } from 'components/RangeSelector';

export type FormFields = {
  standardMarkupAdult: string;
  standardMarkupChild: string;
  infantTripPrice: string;
  advancePayment: string;
  defaultTransferPrice: string;
  descriptionTitle: string;
  notesTitle: string;
  durationRanges: Range[];
  revalidateStaticContent: string;
  cancelOrdersAfterMinutes: string;
  autosaveDelay: string;
  priceCheckerLimit: string;
  adminEmailErrors: string;
  adminEmailNewsSubscribers: string;
  adminEmailOrders: string;
  abandonedCheckoutTimeout: string;
  orderCleanupAge: string;

  smtpName: string;
  smtpEmail: string;
  smtpServer: string;
  smtpPort: string;
  smtpUser: string;
  smtpPassword: string;
  smtpType: SmtpType | null;

  seoHomeTitle: string;
  seoHomeMetaDescription: string;
  titleSuffix: string;
  tripsMetaTitle: string;
  tripsMetaDescription: string;
  tripsH1: string;

  facebookAppId: string;
  schemaProviderDetails: {
    name: string;
    image: string;
    pricerange: string;
    phone: string;
    address: string;
    city: string;
    country: string;
    postcode: string;
    logo: string;
  };

  flightsProvider: FlightsProvider | null;
  flightsApiLimit: string;
  priceChangeTreshold: string;
  flightsUpdaterEnabled: boolean;
  flightBookingEnabled: boolean;
  updateFlightsPerMinute: string;
  inactiveUpdaterWarningThreshold: string;
  keepFlightPriceHistoryDays: string;

  debugMode: boolean;
  profiler: boolean;
};

type Props = {
  settings: GeneralSettingsEditForm_settings$key;
  loading?: boolean;
  onSubmit: (values: FormFields) => void;
};

const GeneralSettingsEditForm: FC<Props> = props => {
  const { onSubmit, loading, ...rest } = props;

  const data = useFragment(
    graphql`
      fragment GeneralSettingsEditForm_settings on Settings {
        # GENERAL
        standardMarkupAdult
        standardMarkupChild
        infantTripPrice
        advancePayment
        defaultTransferPrice
        descriptionTitle
        notesTitle
        durationRanges {
          start
          end
        }
        revalidateStaticContent
        cancelOrdersAfterMinutes
        autosaveDelay
        priceCheckerLimit
        adminEmailErrors
        adminEmailNewsSubscribers
        adminEmailOrders
        abandonedCheckoutTimeout
        orderCleanupAge

        #SMTP
        smtpName
        smtpEmail
        smtpServer
        smtpPort
        smtpUser
        smtpPassword
        smtpType

        #SEO
        seoHomeTitle
        seoHomeMetaDescription
        titleSuffix
        tripsMetaTitle
        tripsMetaDescription
        tripsH1

        #OPEN GRAPH
        facebookAppId
        schemaProviderDetails {
          name
          image
          pricerange
          phone
          address
          city
          country
          postcode
          logo
        }

        #FLIGHTS
        flightsProvider
        flightsApiLimit
        priceChangeTreshold
        flightsUpdaterEnabled
        flightBookingEnabled
        updateFlightsPerMinute
        keepFlightPriceHistoryDays
        inactiveUpdaterWarningThreshold

        #SYSTEM
        debugMode
        profiler
      }
    `,
    rest.settings
  );

  const initial: FormFields = {
    standardMarkupAdult: textFieldValue(data.standardMarkupAdult),
    standardMarkupChild: textFieldValue(data.standardMarkupChild),
    infantTripPrice: textFieldValue(data.infantTripPrice),
    advancePayment: textFieldValue(data.advancePayment),
    defaultTransferPrice: textFieldValue(data.defaultTransferPrice),
    descriptionTitle: data.descriptionTitle ?? '',
    notesTitle: data.notesTitle ?? '',
    durationRanges: (data.durationRanges as any) ?? [],
    revalidateStaticContent: textFieldValue(data.revalidateStaticContent),
    cancelOrdersAfterMinutes: textFieldValue(data.cancelOrdersAfterMinutes),
    autosaveDelay: textFieldValue(data.autosaveDelay),
    priceCheckerLimit: textFieldValue(data.priceCheckerLimit),
    adminEmailErrors: data.adminEmailErrors ?? '',
    adminEmailNewsSubscribers: data.adminEmailNewsSubscribers ?? '',
    adminEmailOrders: data.adminEmailOrders ?? '',
    abandonedCheckoutTimeout: textFieldValue(data.abandonedCheckoutTimeout),
    orderCleanupAge: textFieldValue(data.orderCleanupAge),

    smtpName: data.smtpName ?? '',
    smtpEmail: data.smtpEmail ?? '',
    smtpServer: data.smtpServer ?? '',
    smtpPort: textFieldValue(data.smtpPort),
    smtpUser: data.smtpUser ?? '',
    smtpPassword: data.smtpPassword ?? '',
    smtpType: data.smtpType,

    seoHomeTitle: data.seoHomeTitle ?? '',
    seoHomeMetaDescription: data.seoHomeMetaDescription ?? '',
    titleSuffix: data.titleSuffix ?? '',
    tripsMetaTitle: data.tripsMetaTitle ?? '',
    tripsMetaDescription: data.tripsMetaDescription ?? '',
    tripsH1: data.tripsH1 ?? '',

    facebookAppId: data.facebookAppId ?? '',
    schemaProviderDetails: {
      name: data.schemaProviderDetails?.name ?? '',
      image: data.schemaProviderDetails?.image ?? '',
      pricerange: data.schemaProviderDetails?.pricerange ?? '',
      phone: data.schemaProviderDetails?.phone ?? '',
      address: data.schemaProviderDetails?.address ?? '',
      city: data.schemaProviderDetails?.city ?? '',
      country: data.schemaProviderDetails?.country ?? '',
      postcode: data.schemaProviderDetails?.postcode ?? '',
      logo: data.schemaProviderDetails?.logo ?? '',
    },

    flightsProvider: data.flightsProvider,
    flightsApiLimit: textFieldValue(data.flightsApiLimit),
    priceChangeTreshold: textFieldValue(data.priceChangeTreshold),
    flightsUpdaterEnabled: data.flightsUpdaterEnabled ?? false,
    flightBookingEnabled: data.flightBookingEnabled ?? false,
    updateFlightsPerMinute: textFieldValue(data.updateFlightsPerMinute),
    keepFlightPriceHistoryDays: textFieldValue(data.keepFlightPriceHistoryDays),
    inactiveUpdaterWarningThreshold: textFieldValue(
      data.inactiveUpdaterWarningThreshold
    ),

    debugMode: data.debugMode ?? false,
    profiler: data.profiler ?? false,
  };

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={onSubmit}>
      <Form loading={loading}>
        <AccordionController id="generalSettings">
          <General />
          <Smtp />
          <Seo />
          <OpenGraph />
          <Flights />
          <System />
        </AccordionController>
      </Form>
    </Formik>
  );
};

export default GeneralSettingsEditForm;
